import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Hotel, ShieldCheck, MapPin, Search, BadgePercent } from "lucide-react";
import api from "../lib/api";
import { Spinner } from "../components/common";
import { HPHeader } from "../components/hotelspulse/HPHeader";
import { HPFooter } from "../components/hotelspulse/HPFooter";

const TYPE_LABEL = (t) => (t || "").replace(/_/g, " ");

export default function HotelsPulseBrowse() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const city = params.get("city") || "";
  const state = params.get("state") || "";
  const ptype = params.get("type") || "";

  useEffect(() => { api.get("/directstay/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const sp = new URLSearchParams();
    if (city) sp.set("city", city); if (state) sp.set("state", state);
    if (ptype) sp.set("property_type", ptype); if (params.get("q")) sp.set("q", params.get("q"));
    api.get(`/directstay/properties?${sp}`).then((r) => setData(r.data)).catch(() => setData({ items: [], states: [] }));
  }, [city, state, ptype, params]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  if (!data) return <Spinner full />;

  return (
    <div data-testid="hotelspulse-browse">
      <HPHeader />
      <section className="bg-emerald-950 text-white border-b border-emerald-900">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-3 flex items-center gap-2"><Hotel className="w-4 h-4" /> HotelsPulse Verified Network</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight">Independent stays, direct rates</h1>
          <p className="text-zinc-300 mt-3 max-w-2xl">Every property is verified and hotel-managed. Send your inquiry directly — no OTA commission built into your price.</p>
          <form className="mt-6 flex max-w-xl" onSubmit={(e) => { e.preventDefault(); setFilter("q", q); }}>
            <input data-testid="browse-search" className="flex-1 px-4 py-3 text-zinc-900 text-sm focus:outline-none" placeholder="Search by property or city…" value={q} onChange={(e) => setQ(e.target.value)} />
            <button data-testid="browse-search-btn" className="bg-amber-400 text-emerald-950 px-5 font-700"><Search className="w-4 h-4" /></button>
          </form>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          <select data-testid="filter-state" className="border border-zinc-300 px-3 py-2 text-sm bg-white" value={state} onChange={(e) => setFilter("state", e.target.value)}>
            <option value="">All states</option>
            {(data.states || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-type" className="border border-zinc-300 px-3 py-2 text-sm bg-white" value={ptype} onChange={(e) => setFilter("type", e.target.value)}>
            <option value="">All property types</option>
            {(config?.property_types || []).map((t) => <option key={t} value={t}>{TYPE_LABEL(t)}</option>)}
          </select>
          {city && <button onClick={() => setFilter("city", "")} className="border border-emerald-600 text-emerald-700 px-3 py-2 text-sm">City: {city} ✕</button>}
        </div>

        {data.items.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-16 text-center">
            <Hotel className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <div className="font-heading font-900 text-xl">No verified properties here yet</div>
            <p className="text-zinc-500 mt-1">We're onboarding founding partners now. Own a property?</p>
            <Link to="/hotelspulse" className="inline-block mt-4 bg-emerald-700 text-white font-700 px-6 py-3 text-sm uppercase">Join the pilot free</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.items.map((p) => (
              <Link key={p.id} to={`/hotelspulse/stay/${p.slug}`} data-testid={`prop-card-${p.slug}`} className="border border-zinc-300 bg-white hover:border-emerald-600 transition-colors group">
                {p.photos?.[0] ? (
                  <img src={p.photos[0]} alt={p.name} className="w-full h-44 object-cover" />
                ) : (
                  <div className="w-full h-44 bg-emerald-50 flex items-center justify-center"><Hotel className="w-10 h-10 text-emerald-200" /></div>
                )}
                <div className="p-4">
                  <div className="text-[10px] font-700 uppercase tracking-wide text-emerald-700">{TYPE_LABEL(p.property_type)}</div>
                  <div className="font-heading font-900 text-lg group-hover:text-emerald-700 transition-colors">{p.name}</div>
                  <div className="text-sm text-zinc-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" /> {p.location?.city}, {p.location?.state}</div>
                  <div className="flex items-center gap-3 mt-3 text-xs">
                    {(p.badges || []).includes("hotel_managed") && <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3.5 h-3.5" /> Hotel-managed</span>}
                    {p.offers_count > 0 && <span className="inline-flex items-center gap-1 text-amber-600"><BadgePercent className="w-3.5 h-3.5" /> {p.offers_count} direct offer{p.offers_count > 1 ? "s" : ""}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <HPFooter />
    </div>
  );
}
